import React, { FC } from 'react'

import styled from 'styled-components'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'
import { Skeleton } from 'antd'
import { useMediaQuery } from 'react-responsive'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import MetaAdsIcon from 'src/Assets/Svg/platform-icons/MetaAds.svg?react'

import { ResourcePreview } from './ResourcePreview'

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  flex-basis: calc(50% - 48px);

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
    border-radius: 16px;
    flex-basis: unset;
  }
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Header = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize18};
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const Description = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    display: none;
  }
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    width: 120px;
    border-radius: 16px;
  }
  & > .ant-skeleton-input {
    min-width: 120px !important;
    width: 120px !important;
    height: 48px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;
    @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
      width: 240px !important;
    }
  }
`

export interface MetaAdsResourceDetailsProps {
  platform?: CampaignPlatformTypeEnum
  coverUrl?: string
  resourceName?: string
  ownerName?: string
  duration?: string
  trackPreviewUrl?: string
  isResourceDataLoading?: boolean
  isPlaylist?: boolean
}

export const MetaAdsResourceDetails: FC<MetaAdsResourceDetailsProps> = ({
  coverUrl,
  resourceName,
  ownerName,
  duration,
  trackPreviewUrl,
  isResourceDataLoading,
  isPlaylist,
}) => {
  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH })

  if (isResourceDataLoading) {
    return <StyledSkeleton />
  }

  return (
    <Container>
      {!isMobile && (
        <IconWrapper>
          <MetaAdsIcon width={24} />
        </IconWrapper>
      )}

      <DescriptionWrapper>
        <HeaderWrapper>
          {isMobile && (
            <IconWrapper>
              <MetaAdsIcon width={24} />
            </IconWrapper>
          )}

          <Header>New Meta Ads Campaign</Header>
        </HeaderWrapper>

        <Description>
          Ready to amplify your music's exposure? Fill in the contact details to
          access a world of promotional opportunities!
        </Description>
      </DescriptionWrapper>
      <ResourcePreview
        coverUrl={coverUrl}
        resourceName={resourceName}
        ownerName={ownerName}
        duration={duration}
        trackPreviewUrl={trackPreviewUrl}
        isPlaylist={isPlaylist}
      />
    </Container>
  )
}
